/**
 * Prototype Index / Navigator
 * Lists all available test prototypes for easy navigation
 *
 * Access: http://flownotes.localhost/?test=index
 */

import { ExternalLink, Code, FileEdit, MessageSquare, Brain, Layout, Mic, PanelRight, Clock, TrendingUp, FolderOpen, ListTodo, Building2, Navigation, BookOpen, Inbox, Zap, Smartphone, Filter, Bot, ShieldCheck, Flag, Sparkles, CheckSquare, Tent, Users, GraduationCap, FileText, Rss, FolderTree } from 'lucide-react';
import { GoogleDriveIcon } from '@/components/icons/google-drive-icon';
import { Button } from '@/components/ui/button';

interface Prototype {
  id: string;
  name: string;
  description: string;
  url: string;
  icon: React.ReactNode;
  status: 'active' | 'experimental' | 'complete' | 'superseded';
  /** Required when status is superseded, e.g. 'maater-ui#panel-collapse'. */
  supersededBy?: string;
  /** Feature demo still current for its content, but its shell chrome is not. */
  shellSupersededBy?: string;
  lastModified: Date;
}

// File modification dates from the actual test component files
const prototypes: Prototype[] = [
  {
    id: 'maater-ui',
    name: 'Maater UI gold standard',
    description:
      'Agent SoT gallery: open destinations, list gestures, entity menus, reader chrome, index vs entity tabs, wait/sync, mode-panel stack, who\'s here (art, #presence). Skill: maater-design-ui. Surface demos consume these decisions.',
    url: '/?test=maater-ui',
    icon: <Layout className="w-5 h-5" />,
    status: 'active',
    lastModified: new Date('2026-08-01T00:00:00'),
  },
  {
    id: 'doc-comments',
    name: 'In-doc comments + suggestions',
    description:
      'Figma gutter badges + Docs Comments rail on real Speedy work-spec prose (headings, long paras, tables, lists, code); @user/@agent tags; sentence-range marks. Bet: 2026-05-22-multi-user-communication. Shell: ?test=maater-ui.',
    url: '/?test=doc-comments',
    icon: <MessageSquare className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-08-02T00:00:00'),
  },
  {
    id: 'workspace-settings-ia',
    name: 'Workspace settings IA',
    description:
      'Nested Workspaces > Workspace > Extension, sticky breadcrumbs, settings search jump index, ExtensionSettingsShell. Bet: 2026-07-09-simplify-workspace-settings-ia.',
    url: '/?test=workspace-settings-ia',
    icon: <Building2 className="w-5 h-5" />,
    status: 'active',
    lastModified: new Date('2026-07-25T00:00:00'),
  },
  {
    id: 'browse-desk-sidebar',
    name: 'Browse My documents + extension rail',
    description:
      'My documents desk landing + Documents tree; Basecamp on icon rail beside Drive (workspace-enablement gate). ADR-0019 / bet 2026-07-23-browse-my-documents-and-extension-icon-rail.',
    url: '/?test=browse-desk-sidebar',
    icon: <FolderOpen className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-07-23T00:00:00'),
  },
  {
    id: 'matter-feed',
    name: 'Matter Feed',
    description:
      'Sovereign curated feed Phase 0: topic buckets, provenance (source + flow slug), Enough for now batch endpoint, thumbs/save/dismiss, spawn note/todo. Bet: the-matter-feed-sovereign-curated-feed-via-flows.',
    url: '/?test=matter-feed',
    icon: <Rss className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-07-19T00:00:00'),
  },
  {
    id: 'viewer-frontmatter-bar',
    name: 'Viewer Frontmatter Bar',
    description:
      'VSIX Frontmatter bar in the reading column; side-by-side margin vs content TOC pill breakpoints. Bet: 2026-07-16-webapp-viewer-frontmatter-bar.',
    url: '/?test=viewer-frontmatter-bar',
    icon: <FileText className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-07-18T00:00:00'),
  },
  {
    id: 'tts-lesson-audio',
    name: 'TTS lesson audio',
    description:
      'Generate / play / stale / error / locker MP3 chrome for yaml audio fences. Download hands off to BookPlayer. Bet: 2026-09-15-tts-lesson-audio-generate-store-play.',
    url: '/?test=tts-lesson-audio',
    icon: <Mic className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-09-19T00:00:00'),
  },
  {
    id: 'workspace-activity',
    name: 'Search + Activity Continuity',
    description:
      'Search host + Activity named view (All edits), density toggle, saved-queries rail vs inbox folders. Continuity guide: docs/design/maater-list-surface-continuity.md. Paper/Ink/Stone.',
    url: '/?test=workspace-activity',
    icon: <Users className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-07-17T00:00:00'),
  },
  {
    id: 'inbox-folders',
    name: 'Inbox Folders Continuity',
    description:
      'Surface demo (decisions live in ?test=maater-ui): folders rail + thread list, PanelRight preview, reader chrome. Continuity guide: docs/design/maater-list-surface-continuity.md.',
    url: '/?test=inbox-folders',
    icon: <Inbox className="w-5 h-5" />,
    status: 'experimental',
    shellSupersededBy: 'maater-ui#panel-collapse',
    lastModified: new Date('2026-07-17T00:00:00'),
  },
  {
    id: 'basecamp-extension',
    name: 'Basecamp Extension Chrome (v2)',
    description:
      'Full screen inventory + Projects focus (live/cached Basecamp). Settings Listening + Activity mocks. Bets: chrome surfaces + Triggers/Listening.',
    url: '/?test=basecamp-extension',
    icon: <Tent className="w-5 h-5" />,
    status: 'experimental',
    shellSupersededBy: 'maater-ui#panel-collapse',
    lastModified: new Date('2026-07-12T06:30:00'),
  },
  {
    id: 'basecamp-project-folder-mappings',
    name: 'Basecamp Project Folders (Settings)',
    description:
      'Locked chrome for Settings > Extensions > Basecamp > Project folders: default ingest folder, compact Add project dropdown, slide-expand override cards, list_id -> folder rules, member read-only. Bet: 2026-08-27.',
    url: '/?test=basecamp-project-folder-mappings',
    icon: <FolderTree className="w-5 h-5" />,
    status: 'active',
    lastModified: new Date('2026-08-28T00:00:00'),
  },
  {
    id: 'asana-extension',
    name: 'Asana Extension',
    description:
      'Browse-nested Asana limb: Settings connect, Add project, House Sale sections/tasks, task viewer, create work spec (slug filename + GID frontmatter), linked doc badge. Bet: 2026-04-16.',
    url: '/?test=asana-extension',
    icon: <CheckSquare className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-07-12T06:00:00'),
  },
  {
    id: 'ai-rename-suggest',
    name: 'AI Rename Suggest',
    description:
      'Shared RenameField: sparkle preview/shuffle, date + Basecamp todo chips, slim doc modal. Chat sidebar + doc surfaces. Bet: 2026-07-10-ai-rename-suggest.',
    url: '/?test=ai-rename-suggest',
    icon: <Sparkles className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-07-10T07:00:00'),
  },
  {
    id: 'streaming-agent',
    name: 'Streaming Agent UI',
    description:
      'Approved direction: mid-turn shimmer, file-edit/create compact rows, cap+continue, historical collapse. Minimal chrome + rich prose. Bet: 2026-07-09 redesign streaming agent UI/UX.',
    url: '/?test=streaming-agent',
    icon: <MessageSquare className="w-5 h-5" />,
    status: 'active',
    lastModified: new Date('2026-07-09T18:15:00'),
  },
  {
    id: 'flows-studio',
    name: 'Flows Studio (v4.32)',
    description:
      'Surface demo. Content is the QA-passed v4.15 studio (Definition / Runs, runtime dropdown, Gallery, Pulse). Shell chrome comes from ?test=maater-ui#panel-collapse. Copy the studio content, not this frame.',
    url: '/?test=flows-studio',
    icon: <Zap className="w-5 h-5" />,
    status: 'experimental',
    shellSupersededBy: 'maater-ui#panel-collapse',
    lastModified: new Date('2026-10-29T00:00:00'),
  },
  {
    id: 'feedback-modal',
    name: 'Pilot Feedback Modal',
    description:
      'Structured FeedbackModal + POST /api/pilot-feedback: category, description, rating, session context, message snapshot. Bet: 2026-06-08 pilot chat feedback.',
    url: '/?test=feedback-modal',
    icon: <Flag className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-06-20T12:00:00'),
  },
  {
    id: 'document-version-history',
    name: 'Document Version History',
    description:
      'Version history: day headers, same-minute time clusters (collapsed summary), nested edits, OS-aware mod+click compare. Bet: 2026-06-04-document-version-history-view.',
    url: '/?test=document-version-history',
    icon: <Clock className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-07-22T00:00:00'),
  },
  {
    id: 'knowledge-hub-changelog',
    name: 'Knowledge Hub - Review Changelog',
    description:
      'Document lifecycle review: status strip (verified / review-requested / draft), review-changelog secondary panel timeline, diff-since-verify footer. Bet: 2026-06-04.',
    url: '/?test=knowledge-hub-changelog',
    icon: <ShieldCheck className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-06-05T12:00:00'),
  },
  {
    id: 'quiz',
    name: 'Quiz & LMS Renderer',
    description:
      'kind:quiz document surface: Preview / Quiz (timer + keyboard) / Results / Runs. Local mock scoring - no API. Bet: 2026-07-15 Quiz & LMS Extension.',
    url: '/?test=quiz',
    icon: <GraduationCap className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-07-16T07:00:00'),
  },
  {
    id: 'google-drive-extension',
    name: 'Google Drive Continuity',
    description:
      'Surface demo (decisions live in ?test=maater-ui): Drive/Activity/Linked + Sync mid-frames on Proto chrome. Bet: 2026-04-22; continuity 2026-07-17.',
    url: '/?test=google-drive-extension',
    icon: <GoogleDriveIcon className="w-5 h-5" />,
    status: 'experimental',
    shellSupersededBy: 'maater-ui#panel-collapse',
    lastModified: new Date('2026-07-18T00:00:00'),
  },
  {
    id: 'nextcloud-extension',
    name: 'Nextcloud Extension Phase 1a',
    description:
      'Mock prototype: Settings connect panel, Browse tree (All files/Shared/Shares out), file preview. Frontend only, no backend. Follows Drive patterns. Bet: 2026-09-13.',
    url: '/?test=nextcloud-extension',
    icon: <FolderTree className="w-5 h-5" />,
    status: 'experimental',
    shellSupersededBy: 'maater-ui#panel-collapse',
    lastModified: new Date('2026-10-24T00:00:00'),
  },
  {
    id: 'agents-layout',
    name: 'Agents Chat Layout',
    description:
      'Cursor-style agents: conversation history in left sidebar, chat as editor tabs or right agent panel, Open chats in editor toggle, move between surfaces with draft preservation.',
    url: '/?test=agents-layout',
    icon: <Bot className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-05-29T22:00:00'),
  },
  {
    id: 'browse-folder-focus',
    name: 'Browse Focus (hybrid)',
    description:
      'Clients mock (menus, show hidden, 7/22) + Specs tree tab (production node: files, animated gutter, inline show hidden). ?test=browse-level-focus opens Specs tab.',
    url: '/?test=browse-folder-focus',
    icon: <Filter className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-05-27T14:00:00'),
  },
  {
    id: 'mobile-app',
    name: 'Mobile App',
    description: 'iPhone-frame web mock: inbox, thread detail, hub sheet, chat, Browse (folder tree + mock search), capture. Native Expo app in repo mobile/.',
    url: '/?test=mobile-app',
    icon: <Smartphone className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-05-09T11:58:00'),
  },
  {
    id: 'flow-editor',
    name: 'Flow editor / manager',
    description: 'Scenario-first operator prototype: flow inventory, incident queue, run-aware detail pane, routing recovery workflow, and inbox deep-link context.',
    url: '/?test=flow-editor',
    icon: <Zap className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-04-25T14:50:00')
  },
  {
    id: 'inbox-split',
    name: 'Inbox — Conversation Index',
    description: 'Three-pane inbox: browse rail, thread feed with kind filters + ⋯ menu, split reader with voice playback, Cmd+F search, keyboard hints.',
    url: '/?test=inbox-split',
    icon: <Inbox className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-04-23T00:00:00')
  },
  {
    id: 'doc-toc-ruler',
    name: 'Document TOC + Overview Ruler',
    description: 'Sticky "current section" pill trigger, sliding TOC panel with active heading tracking, and custom Overview Ruler replacing the native scrollbar.',
    url: '/?test=doc-toc-ruler',
    icon: <BookOpen className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-04-16T10:00:00')
  },
  {
    id: 'organization-management',
    name: 'Organization Management',
    description: 'Org switcher, toggle on/off, create/manage orgs - multi-org model with session scope',
    url: '/?test=organization-management',
    icon: <Building2 className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-02-20T12:00:00')
  },
  {
    id: 'browse-nav',
    name: 'Browse Nav v2 — Split Panel',
    description: 'Workspace sidebar with Basecamp/Documents as mode selectors that open a resizable lower panel. Global Open Tabs section at top.',
    url: '/?test=browse-nav',
    icon: <Navigation className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-03-11T14:00:00')
  },
  {
    id: 'browse-nav-inline',
    name: 'Browse Nav v3 — Inline Expansion',
    description: 'Alternative concept: Basecamp, Documents, and extensions expand inline within the workspace section. No bottom panel split, no repeated workspace names.',
    url: '/?test=browse-nav-inline',
    icon: <Navigation className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-03-13T10:00:00')
  },
  {
    id: 'basecamp-todos',
    name: 'Basecamp Todos — View Design',
    description: 'Todos table with scope/status filters, Spec/Activity columns, weekly triage preset. Supersedes old basecamp-triage prototype.',
    url: '/?test=basecamp-todos',
    icon: <ListTodo className="w-5 h-5" />,
    status: 'experimental',
    shellSupersededBy: 'maater-ui#panel-collapse',
    lastModified: new Date('2026-03-18T00:00:00')
  },
  {
    id: 'basecamp-projects-cache',
    name: 'Basecamp Projects — Cache sync',
    description:
      'Ok/partial/none cache depth; view ... for Reload/Refresh; status + (i) on bottom bar. SoT: maater-ui#list-status-chrome.',
    url: '/?test=basecamp-projects-cache',
    icon: <FolderOpen className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-08-18T00:00:00'),
  },
  {
    id: 'folder-dashboard',
    name: 'Folder Dashboard',
    description: 'Widget/shortcode system for folder README dashboards with Basecamp integration and AI flows. Older grid prototype. In-note blocks live at structured-doc-blocks.',
    url: '/?test=folder-dashboard',
    icon: <FolderOpen className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-01-29T19:30:00')
  },
  {
    id: 'structured-doc-blocks',
    name: 'Structured doc kits',
    description: 'In-note fences rendered by named kits. Core is built in. Review is an optional import. Specimen: Hop Studios soul audit.',
    url: '/?test=structured-doc-blocks',
    icon: <FileText className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-11-02T21:30:00')
  },
  {
    id: 'cursor-workspace',
    name: 'Cursor Workspace Generator',
    description: 'Test Cursor URL protocol and dynamic workspace generation for task handoffs',
    url: '/?test=cursor-workspace',
    icon: <ExternalLink className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-01-23T06:30:00')
  },
  {
    id: 'product-management',
    name: 'Product Management',
    description: 'Dashboard, roadmap, and cycle tracking - visual interface for product management system',
    url: '/?test=product-management',
    icon: <TrendingUp className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-01-07T14:30:00')
  },
  {
    id: 'chat-window',
    name: 'Chat Window (Thinking Mode)',
    description:
      'Now / Next / Live vs History: noisy live tools vs Thinking scroll cell vs quiet history reopen. Bet: 2026-07-24-chat-live-thinking-timeline-mobile-web. Legacy: /?test=chat-window-legacy',
    url: '/?test=chat-window',
    icon: <MessageSquare className="w-5 h-5" />,
    status: 'active',
    lastModified: new Date('2026-07-19T12:00:00')
  },
  {
    id: 'quiz-domain-escape',
    name: 'Quiz domain escape hatch',
    description:
      'Wireframe: session header with Exit session + abandon-exam confirm. Bet: 2026-07-24-quiz-domain-escape-hatch.',
    url: '/?test=quiz-domain-escape',
    icon: <GraduationCap className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-07-24T12:00:00')
  },
  {
    id: 'mobile-composer-d',
    name: 'Mobile composer D (queue / attach / feedback)',
    description:
      'Wireframes for queue-while-streaming, + attach sheet, pilot feedback sheet. Bets 2026-07-24 mobile D cluster.',
    url: '/?test=mobile-composer-d',
    icon: <Smartphone className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-07-24T19:00:00')
  },
  {
    id: 'chat-queue-edit',
    name: 'Chat queue edit',
    description:
      'Web queue edit: pencil loads the row into the composer under an Editing queued message bar. Cancel restores the follow-up draft. Bet: 2026-10-23-chat-queue-send-now.',
    url: '/?test=chat-queue-edit',
    icon: <FileEdit className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2026-10-25T21:40:00')
  },
  {
    id: 'voice-memos',
    name: 'Voice Memos',
    description: 'Drag-and-drop voice memo management with audio playback and status tracking',
    url: '/?test=voice-memos',
    icon: <Mic className="w-5 h-5" />,
    status: 'active',
    lastModified: new Date('2025-12-07T22:01:00')
  },
  {
    id: 'tab-redesign',
    name: 'Tab Redesign',
    description: 'Chrome-like tab interface with improved visual connection and pinned tabs',
    url: '/?test=tab-redesign',
    icon: <Layout className="w-5 h-5" />,
    status: 'active',
    lastModified: new Date('2025-11-30T23:02:00')
  },
  {
    id: 'secondary-panel',
    name: 'Secondary Panel',
    description: 'Promotion/demotion pattern for secondary panel content (comments, related, work specs)',
    url: '/?test=secondary-panel',
    icon: <PanelRight className="w-5 h-5" />,
    status: 'active',
    lastModified: new Date('2025-11-30T22:48:00')
  },
  {
    id: 'thinking-mode',
    name: 'Thinking Mode',
    description: 'Cursor-inspired reasoning UI with real-time operation display',
    url: '/?test=thinking-mode',
    icon: <Brain className="w-5 h-5" />,
    status: 'experimental',
    lastModified: new Date('2025-11-28T00:15:00')
  },
  {
    id: 'change-tracking',
    name: 'Change Tracking',
    description: 'Visual change indicators (gutter markers, widgets, undo/keep buttons)',
    url: '/?test=change-tracking',
    icon: <FileEdit className="w-5 h-5" />,
    status: 'complete',
    lastModified: new Date('2025-11-28T00:09:00')
  },
  {
    id: 'editor',
    name: 'Editor Test',
    description: 'CodeMirror editor testing and configuration',
    url: '/?test=editor',
    icon: <Code className="w-5 h-5" />,
    status: 'active',
    lastModified: new Date('2025-11-28T00:09:00')
  }
];

export function PrototypeIndex() {
  const handleOpen = (url: string) => {
    window.location.href = url;
  };

  const getStatusBadge = (status: Prototype['status']) => {
    switch (status) {
      case 'complete':
        return <span className="px-2 py-0.5 text-xs rounded bg-green-500/10 text-green-500 border border-green-500/20">Complete</span>;
      case 'active':
        return <span className="px-2 py-0.5 text-xs rounded bg-blue-500/10 text-blue-500 border border-blue-500/20">Active</span>;
      case 'experimental':
        return <span className="px-2 py-0.5 text-xs rounded bg-yellow-500/10 text-yellow-500 border border-yellow-500/20">Experimental</span>;
      case 'superseded':
        return <span className="px-2 py-0.5 text-xs rounded bg-gray-500/10 text-gray-500 border border-gray-500/20">Superseded</span>;
    }
  };

  const getRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);

    if (diffSecs < 60) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`;
    if (diffMonths < 12) return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;

    return date.toLocaleDateString();
  };

  // Sort prototypes by most recent first
  const sortedPrototypes = [...prototypes].sort((a, b) =>
    b.lastModified.getTime() - a.lastModified.getTime()
  );

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="border-b p-6 bg-sidebar">
        <h1 className="text-2xl font-bold mb-2">FlowNotes Prototype Navigator</h1>
        <p className="text-sm text-muted-foreground">
          Quick access to all UI prototypes and test pages
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto">
          {/* Stats bar */}
          <div className="mb-6 flex items-center gap-4 text-sm text-muted-foreground">
            <span>{sortedPrototypes.length} prototypes</span>
            <span>•</span>
            <span>{sortedPrototypes.filter(p => p.status === 'active').length} active</span>
            <span>•</span>
            <span>{sortedPrototypes.filter(p => p.status === 'experimental').length} experimental</span>
            <span>•</span>
            <span>{sortedPrototypes.filter(p => p.status === 'complete').length} complete</span>
            <span className="ml-auto text-xs">Sorted by most recent activity</span>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {sortedPrototypes.map((prototype) => (
              <div
                key={prototype.id}
                className={`border border-border rounded-lg p-4 hover:border-primary/50 transition-colors bg-card${prototype.status === 'superseded' ? ' opacity-60' : ''}`}
                data-proto-status={prototype.status}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-muted">
                      {prototype.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-base">{prototype.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {getStatusBadge(prototype.status)}
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground mb-3">
                  {prototype.description}
                </p>

                {prototype.status === 'superseded' && prototype.supersededBy && (
                  <p className="mb-3 rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-xs text-amber-700 dark:text-amber-300">
                    Superseded by {prototype.supersededBy}. Do not copy this chrome.
                  </p>
                )}
                {prototype.shellSupersededBy && (
                  <p className="mb-3 rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-xs text-amber-700 dark:text-amber-300">
                    Surface demo. Shell chrome (rail, headers, peek row) is superseded by{' '}
                    {prototype.shellSupersededBy}. Copy content, not chrome.
                  </p>
                )}

                {/* Last modified timestamp */}
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Updated {getRelativeTime(prototype.lastModified)}</span>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => handleOpen(prototype.url)}
                    className="flex-1"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Prototype
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(`http://flownotes.localhost${prototype.url}`);
                      alert('URL copied to clipboard!');
                    }}
                  >
                    Copy URL
                  </Button>
                </div>

                <div className="mt-3 pt-3 border-t border-border">
                  <code className="text-xs text-muted-foreground font-mono">
                    {prototype.url}
                  </code>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Links */}
          <div className="mt-8 p-4 border border-border rounded-lg bg-muted/30">
            <h3 className="font-semibold mb-3">Quick Links</h3>
            <div className="flex flex-wrap gap-2">
              {sortedPrototypes.map((prototype) => (
                <button
                  key={prototype.id}
                  onClick={() => handleOpen(prototype.url)}
                  className="px-3 py-1.5 text-sm rounded bg-background border border-border hover:border-primary/50 transition-colors"
                >
                  {prototype.name}
                </button>
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="mt-6 p-4 border border-border rounded-lg bg-muted/20">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> These prototypes are self-contained test pages for UI development.
              They use mock data and are designed for rapid iteration before backend integration.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

